import './client-details-and-users.scss'

import styled from '@emotion/styled'
import { useState } from 'react'

import { Icon, Space, Tabs, Typography } from '@/components/core-v2'

import { AddBusinessUnit } from '../business-unit-details/add-business-unit'
import { EditClient } from '../client-details/edit-client'
import { EditDfm } from '../client-details/edit-dfm'
import { ClientUsers } from '../client-users/client-users'
import { ClientsState, CurrentUserType } from '../clients/clients.state'
import { DfmsState } from '../clients/dfm.state'
import { useClientsLayout } from '../clients-layout-provider'

export type Props = {
  onBusinessUnitSaved?: () => void
  onClientSaved: () => void
  resetSavedIndicator: () => void
  state: ClientsState | DfmsState
  userType: CurrentUserType
}

type TabId = 'details' | 'users' | 'add-business-unit'

const StyledUsersDetails = styled.div`
  height: calc(100vh - 253px);
  overflow-y: auto;
`

export function ClientDetailsAndUsers({
  onBusinessUnitSaved,
  onClientSaved,
  resetSavedIndicator,
  state,
  userType
}: Props) {
  const [activeTabId, setActiveTabId] = useState<TabId>(state.initialActiveTab)

  const { isPortfolioManagerScreen } = useClientsLayout()

  const tabTitle = isPortfolioManagerScreen ? 'Portfolio manager' : 'Client'
  const clientName = isPortfolioManagerScreen
    ? (state as DfmsState).selectedDfm?.dfmName
    : (state as ClientsState).selectedClient?.clientName

  return (
    <div className="client-details-and-users">
      <div className="client-top-block">
        <Space isflex alignItems="center">
          <Typography fontSize="15px" letterSpacing="0" variant="b3">
            {clientName}
          </Typography>
        </Space>
        <Tabs.List
          className={`mt-4 ${isPortfolioManagerScreen ? '' : 'client-tab-list'}`}
          showRule={false}
          spacing="sm"
        >
          {userType === 'admin' && (
            <Tabs.Tab
              isSelected={activeTabId === 'details'}
              value="details"
              onClick={() => {
                setActiveTabId('details')
                resetSavedIndicator()
              }}
            >
              {`${tabTitle} details`}
            </Tabs.Tab>
          )}
          <Tabs.Tab
            isSelected={activeTabId === 'users'}
            value="users"
            onClick={() => {
              setActiveTabId('users')
              resetSavedIndicator()
            }}
          >
            {`${tabTitle} users`}
          </Tabs.Tab>
          {!isPortfolioManagerScreen && (
            <Tabs.Tab
              isSelected={activeTabId === 'add-business-unit'}
              value="add-business-unit"
              onClick={() => setActiveTabId('add-business-unit')}
            >
              <Icon className="mr-1 client-circle-icon" name="circleAdd" size="medium" /> Add new
              business unit
            </Tabs.Tab>
          )}
        </Tabs.List>
      </div>
      <StyledUsersDetails>
        {activeTabId === 'details' && userType === 'admin' && (
          <Tabs.Panel value="details">
            {isPortfolioManagerScreen ? (
              <EditDfm dfmState={state as DfmsState} onSave={onClientSaved} />
            ) : (
              <EditClient clientState={state as ClientsState} onSave={onClientSaved} />
            )}
          </Tabs.Panel>
        )}
        {activeTabId === 'users' && (
          <Tabs.Panel value="users">
            <ClientUsers
              clientId={
                isPortfolioManagerScreen
                  ? (state as DfmsState).selectedDfm?.dfmId
                  : (state as ClientsState).selectedClient?.clientId
              }
            />
          </Tabs.Panel>
        )}
        {activeTabId === 'add-business-unit' &&
          onBusinessUnitSaved &&
          !isPortfolioManagerScreen && (
            <Tabs.Panel value="add-business-unit">
              <AddBusinessUnit
                client={(state as ClientsState).client}
                clientId={(state as ClientsState).selectedClient?.clientId}
                isSaveSuccess={(state as ClientsState).isBusinessUnitSavedSuccessfully}
                strategies={(state as ClientsState).strategies}
                onSaveSuccess={onBusinessUnitSaved}
              />
            </Tabs.Panel>
          )}
      </StyledUsersDetails>
    </div>
  )
}
